// Mounts the screen a path resolves to (CR-W2a-35): registry match → permission gate → lazy route module →
// the screen's own page layout. Unknown path, forbidden and not-yet-built screens have their own pages; a load or
// render failure is contained to the content area so the rail keeps working.
import { Component, Suspense, useMemo, useState, type ReactNode } from 'react';
import { SCREENS, visibleScreens, type PermissionKey } from '@skm/core';
import { PageLayout, useT } from '@skm/ui';
import type { ApiClient } from '../api/client.ts';
import { matchScreen, recordListScreen } from '../shell/nav.ts';
import { createScreenResolver, type ScreenComponent, type ScreenModuleEntry } from './screens.ts';

interface HostProps {
  readonly path: string;
  readonly api: ApiClient;
  readonly permissions: ReadonlySet<PermissionKey>;
  readonly navigate: (route: string) => void;
  readonly modules: readonly ScreenModuleEntry[];
}

/** A registry screen no lane has built yet (dashboards, costing, …): its own title and a note saying so. */
const notBuilt = (screenId: string): ScreenComponent =>
  function NotBuilt() {
    const t = useT();
    return (
      <PageLayout title={t(`screens.${screenId}`)}>
        <p>{t('frame.notBuilt')}</p>
      </PageLayout>
    );
  };

function LoadFailed({ onRetry }: { readonly onRetry: () => void }) {
  const t = useT();
  return (
    <div role="alert">
      <p>{t('frame.error.title')}</p>
      <button type="button" onClick={onRetry}>
        {t('frame.error.retry')}
      </button>
    </div>
  );
}

interface BoundaryProps {
  readonly onRetry: () => void;
  readonly children: ReactNode;
}

/** Catches a failed route-module load or a screen that throws; remounted (reset) when the path changes. */
class ScreenBoundary extends Component<BoundaryProps, { readonly failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    // No console in app code (lint): hand the error to the browser's global handler (devtools, monitoring).
    if (typeof reportError === 'function') reportError(error);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <LoadFailed
        onRetry={() => {
          this.setState({ failed: false });
          this.props.onRetry();
        }}
      />
    );
  }
}

export function ScreenHost({ path, api, permissions, navigate, modules }: HostProps) {
  const t = useT();
  const [, setAttempt] = useState(0);
  const resolver = useMemo(() => createScreenResolver(modules, notBuilt), [modules]);
  const match = matchScreen(path, SCREENS);
  if (!match) return <PageLayout title={t('frame.notFound')} />;
  const { screen, params } = match;
  if (!visibleScreens(permissions).some((s) => s.id === screen.id))
    return <PageLayout title={t('frame.forbidden')} />;
  // A record route whose module only ships the list screen opens that screen (it shows the record inline).
  const list = recordListScreen(screen, SCREENS);
  const Screen = resolver.component(list ? [screen.id, list.id] : [screen.id]);
  return (
    <ScreenBoundary key={path} onRetry={() => setAttempt((n) => n + 1)}>
      <Suspense fallback={<p role="status">{t('frame.loading')}</p>}>
        {/* The resolver caches one lazy component per screen id, so its identity is stable across renders. */}
        {/* eslint-disable-next-line react-hooks/static-components */}
        <Screen api={api} permissions={permissions} params={params} navigate={navigate} />
      </Suspense>
    </ScreenBoundary>
  );
}
